import React, { useState, useEffect, useRef } from 'react';
import { COLLECTION_LABELS } from '../constants/authorities';

/* ────────────────────────────────────────────────────────────
   formatChunkText(raw) → [{type, content, indent?}]

   Types: paragraph | header | list-item | citation | blank
   ──────────────────────────────────────────────────────────── */

// Citation: must contain § OR start with one of the specific prefixes.
// Does NOT match lines that merely contain a period/decimal (.01, .02).
const CITATION_RE = /§|^Rev\.\s|^Reg\.\s|^PLR\s|^I\.R\.C\.\s|^Treas\.\s*Reg|^IRC\s|^U\.S\.C/;

const LIST_MARKER_RE = /^(\s*)(\([a-zA-Z0-9]{1,4}\)|\(?[ivxlc]{1,5}\)|[0-9]{1,3}\.|[a-zA-Z]\.)\s/;
const ALL_CAPS_RE = /^[A-Z][A-Z\s\-—–,.;:()&/§0-9]{4,}$/;
const HEADING_PREFIX_RE = /^(SECTION|Section|SEC\.|Sec\.|PART|Part|SUBPART|Subpart|CHAPTER|Chapter|ARTICLE|Article)\s/;

// Westlaw / Thomson Reuters boilerplate AND PDF page headers to strip entirely
const WESTLAW_JUNK_RE = /Thomson Reuters|Westlaw|No claim to original|©\s*20/i;
// PDF page-number headers: "Revenue Procedure, Rev. Proc. 2017-13 (2017) 6"
const PAGE_HEADER_RE = /^(Revenue (Procedure|Ruling)|Rev\.\s*(Proc|Rul)\.)\s*,?\s*(Rev\.\s*(Proc|Rul)\.\s*)?\d{4}-\d+\s*\(\d{4}\)\s*\d*\s*$/;

function isHeaderLine(line) {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length < 5) return false;
  // Never treat lines starting with ( or a digit as headers
  if (/^[(\d]/.test(trimmed)) return false;
  // ALL CAPS (5+ chars)
  if (ALL_CAPS_RE.test(trimmed)) return true;
  // SECTION / PART / CHAPTER prefix
  if (HEADING_PREFIX_RE.test(trimmed)) return true;
  return false;
}

function isCitationLine(line) {
  return CITATION_RE.test(line.trim());
}

function isListItem(line) {
  return LIST_MARKER_RE.test(line);
}

function isWestlawJunk(line) {
  if (WESTLAW_JUNK_RE.test(line)) return true;
  if (PAGE_HEADER_RE.test(line.trim())) return true;
  return false;
}

function shouldBreakParagraph(currentLine, nextLine) {
  if (!nextLine) return true;
  const nextTrimmed = nextLine.trim();
  if (!nextTrimmed) return true;
  if (isListItem(nextLine)) return true;
  if (isHeaderLine(nextLine)) return true;
  const curTrimmed = currentLine.trim();
  if (/[.:]$/.test(curTrimmed) && /^[A-Z(]/.test(nextTrimmed)) return true;
  return false;
}

function leadingSpaces(line) {
  const m = line.match(/^(\s+)/);
  return m ? m[1].length : 0;
}

function formatChunkText(raw) {
  if (!raw) return [];
  const lines = raw.split('\n');
  const blocks = [];
  let paraBuffer = [];

  const flushPara = () => {
    if (paraBuffer.length === 0) return;
    const text = paraBuffer.join(' ').replace(/\s+/g, ' ').trim();
    if (text) {
      blocks.push({ type: 'paragraph', content: text });
    }
    paraBuffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nextLine = i + 1 < lines.length ? lines[i + 1] : null;
    const trimmed = line.trim();

    // ── Strip Westlaw/Thomson Reuters boilerplate ──
    if (trimmed && isWestlawJunk(trimmed)) {
      continue;
    }

    // ── Blank line ──
    if (!trimmed) {
      flushPara();
      if (nextLine !== null && !(nextLine.trim())) {
        blocks.push({ type: 'blank', content: '' });
        while (i + 1 < lines.length && !lines[i + 1].trim()) i++;
      }
      continue;
    }

    // ── Header detection (only when not mid-paragraph) ──
    if (isHeaderLine(trimmed) && paraBuffer.length === 0) {
      flushPara();
      blocks.push({ type: 'header', content: trimmed });
      continue;
    }

    // ── List item ──
    const listMatch = line.match(LIST_MARKER_RE);
    if (listMatch) {
      flushPara();
      const indent = leadingSpaces(line);
      blocks.push({ type: 'list-item', content: trimmed, indent });
      continue;
    }

    // ── Citation line (standalone, not mid-paragraph) ──
    if (paraBuffer.length === 0 && isCitationLine(trimmed) && trimmed.length < 120) {
      flushPara();
      blocks.push({ type: 'citation', content: trimmed });
      continue;
    }

    // ── Indented line (>=4 spaces) as standalone block ──
    const indentLevel = leadingSpaces(line);
    if (indentLevel >= 4 && paraBuffer.length === 0) {
      flushPara();
      blocks.push({ type: 'paragraph', content: trimmed, indent: indentLevel });
      continue;
    }

    // ── Normal text: accumulate into paragraph, reflow ──
    paraBuffer.push(trimmed);
    if (shouldBreakParagraph(line, nextLine)) {
      flushPara();
    }
  }
  flushPara();
  return blocks;
}

/* ── Render a single formatted block ── */
function renderBlock(block, idx) {
  switch (block.type) {
    case 'header':
      return <div key={idx} className="dv-header">{block.content}</div>;
    case 'list-item':
      return (
        <div key={idx} className="dv-list-item"
          style={block.indent > 4 ? { paddingLeft: `${Math.min(block.indent * 4, 80)}px` } : undefined}>
          {block.content}
        </div>
      );
    case 'citation':
      return <div key={idx} className="dv-citation">{block.content}</div>;
    case 'blank':
      return <div key={idx} className="dv-blank" />;
    case 'paragraph':
    default:
      return (
        <div key={idx} className="dv-paragraph"
          style={block.indent ? { paddingLeft: `${Math.min(block.indent * 4, 80)}px` } : undefined}>
          {block.content}
        </div>
      );
  }
}

/* ────────────────────────────────────────────────────────────
   mergeChunks(chunks) → string[]

   Joins chunk texts across boundaries when a chunk ends
   mid-sentence (no terminal punctuation). The result is
   fewer, larger logical blocks with complete sentences.
   ──────────────────────────────────────────────────────────── */
const SENTENCE_END_RE = /[.?!:]\s*$/;

function mergeChunks(chunks) {
  if (!chunks || chunks.length === 0) return [];
  const merged = [];
  let buffer = '';

  for (let i = 0; i < chunks.length; i++) {
    const text = (chunks[i].text || '').trim();
    if (!text) continue;

    if (buffer) {
      // Join to previous incomplete chunk with a space
      buffer += ' ' + text;
    } else {
      buffer = text;
    }

    // Flush if this chunk ends with sentence-ending punctuation
    // or if it's the last chunk
    if (SENTENCE_END_RE.test(buffer) || i === chunks.length - 1) {
      merged.push(buffer);
      buffer = '';
    }
  }
  // Flush anything remaining
  if (buffer) merged.push(buffer);
  return merged;
}

/* ────────────────────────────────────────────────────────────
   DocumentViewer Component
   ──────────────────────────────────────────────────────────── */

const DocumentViewer = ({ query, onClose }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lookupInput, setLookupInput] = useState(query || '');
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  const fetchDocument = async (q) => {
    if (!q || !q.trim()) return;
    setLoading(true);
    setError(null);
    setDoc(null);
    try {
      const res = await fetch('/api/document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setDoc(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (query) {
      setLookupInput(query);
      fetchDocument(query);
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [query]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchDocument(lookupInput);
  };

  const escapeHtml = (str) => (str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const title = doc?.title || 'Document';
    const content = document.querySelector('.docviewer-text')?.innerHTML || '';
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Georgia, 'Times New Roman', serif; font-size: 14px; line-height: 1.7; color: #000; background: #fff; margin: 40px; }
          .dv-header { font-weight: bold; margin-top: 1.2em; margin-bottom: 0.4em; }
          .dv-paragraph { margin-bottom: 0.8em; }
          .dv-list-item { padding-left: 20px; margin-bottom: 0.3em; }
          .dv-citation { font-size: 0.9em; color: #555; margin-bottom: 0.5em; }
          .dv-blank { height: 0.6em; }
          h1 { font-size: 18px; margin-bottom: 4px; }
          .meta { font-size: 12px; color: #666; margin-bottom: 20px; border-bottom: 1px solid #ccc; padding-bottom: 10px; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <div class="meta">${escapeHtml(doc?.source || '')} — ${escapeHtml(doc?.collection || '')} — ${doc?.total_chunks || ''} sections</div>
        ${content}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="docviewer-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="docviewer-panel" ref={panelRef}>
        {/* Header */}
        <div className="docviewer-header">
          <form onSubmit={handleSubmit} className="docviewer-search">
            <input
              ref={inputRef}
              type="text"
              value={lookupInput}
              onChange={(e) => setLookupInput(e.target.value)}
              placeholder="Enter authority (e.g. Rev. Proc. 2017-13, PLR 200309003, Portfolio 183)"
              className="docviewer-input"
            />
            <button type="submit" className="docviewer-search-btn" disabled={loading}>
              {loading ? 'Searching...' : 'Retrieve'}
            </button>
          </form>
          <button
            className="docviewer-print"
            onClick={handlePrint}
            title="Print document"
            disabled={!doc}
          >
            Print
          </button>
          <button className="docviewer-close" onClick={onClose} title="Close (Esc)">
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="docviewer-body">
          {loading && (
            <div className="docviewer-loading">
              <div className="docviewer-spinner" />
              <p>Retrieving document...</p>
            </div>
          )}

          {error && (
            <div className="docviewer-error">
              <strong>Document not found</strong>
              <p>{error}</p>
              <p style={{ fontSize: '12px', marginTop: '8px', color: 'var(--text-tertiary)' }}>
                Try a more specific citation (e.g., "Rev. Proc. 2017-13", "PLR 200309003", "Reg. 1.141-3", "Portfolio 183")
              </p>
            </div>
          )}

          {doc && (
            <>
              <div className="docviewer-meta">
                <h2 className="docviewer-title">{doc.title}</h2>
                <div className="docviewer-tags">
                  <span className="docviewer-tag collection">
                    {COLLECTION_LABELS[doc.collection] || doc.collection}
                  </span>
                  {doc.source && doc.source !== doc.collection && (
                    <span className="docviewer-tag source">{doc.source}</span>
                  )}
                  <span className="docviewer-tag chunks">
                    {doc.total_chunks} chunk{doc.total_chunks !== 1 ? 's' : ''}
                  </span>
                  {doc.partial && (
                    <span className="docviewer-tag partial">Partial match</span>
                  )}
                </div>
                {doc.metadata && (
                  <div className="docviewer-metadata-row">
                    {doc.metadata.portfolio_number && <span>Portfolio #{doc.metadata.portfolio_number}</span>}
                    {doc.metadata.publisher && <span>Publisher: {doc.metadata.publisher}</span>}
                    {doc.metadata.year && <span>Year: {doc.metadata.year}</span>}
                    {doc.metadata.author && <span>Author: {doc.metadata.author}</span>}
                  </div>
                )}
              </div>

              <div className="docviewer-text">
                {mergeChunks(doc.chunks).map((text, i) => {
                  const formatted = formatChunkText(text);
                  return (
                    <div key={i} className="docviewer-chunk">
                      <div className="docviewer-chunk-text">
                        {formatted.map((block, j) => renderBlock(block, j))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!loading && !error && !doc && (
            <div className="docviewer-empty">
              <p>Enter an authority name above to retrieve its full text.</p>
              <div className="docviewer-examples">
                <strong>Examples:</strong>
                <ul>
                  <li><button onClick={() => { setLookupInput('Rev. Proc. 2017-13'); fetchDocument('Rev. Proc. 2017-13'); }}>Rev. Proc. 2017-13</button></li>
                  <li><button onClick={() => { setLookupInput('Reg. 1.141-3'); fetchDocument('Reg. 1.141-3'); }}>Treas. Reg. 1.141-3</button></li>
                  <li><button onClick={() => { setLookupInput('Portfolio 183'); fetchDocument('Portfolio 183'); }}>BNA Portfolio 183</button></li>
                  <li><button onClick={() => { setLookupInput('Section 148'); fetchDocument('Section 148'); }}>IRC Section 148</button></li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
